/**
 * The box under the player, **Ask for a change** (docs/SPEC.md §8.1–§8.7).
 *
 * - §8.1's sentence; Enter sends (Shift+Enter is a new line); what goes with
 *   the words (the focused scene, the object clicked, the area, the view,
 *   the marked part) as chips, each of which can be left out.
 * - While a change works: its phase (Changing… / Rebuilding scene k… with the
 *   share of its scenes ready / Writing scene k… about N s, F credits) and
 *   **Stop**, which ends it at once on the page and within a second on the
 *   server (§8.7).
 * - A question (§8.5): `Question.tsx::ChangeQuestion` (a candidate scene of
 *   another film framed in the browser, P28-T10).
 * - The author tier (§8.4): its price and **Write it · F credits** / **Not
 *   now** — asked only at or above `PRICE_CONFIRM_CREDITS` (§15.1).
 * - Prices (§15.1, P28-T08): a change whose estimate reaches
 *   `PRICE_CONFIRM_CREDITS` comes back from the route as a question (202
 *   `{estimate, confirm: "required"}`): the words and "This change — about N
 *   credits. Go ahead?" with **Go ahead** (sent again with `confirm: true`)
 *   and **Not now**; a change the balance cannot pay for (402) is the block
 *   with **Top up**, the words kept in the box. No cost line under answers.
 * - Every answer: the model's `say` as written, then the coded sentences of
 *   `says` from the copy table, the scope line last; a cannot or a decline
 *   as its sentence; Nothing in Objects fits offers **Yes, write it**.
 * - A change that saved the film as a template (`template_card`) opens a
 *   small card with the template's name and slots, linking to it.
 * - While the film builds (§6.1, P28-T09) a change sent waits for it, and
 *   the box says so (`waiting`); before the film is written nothing can be
 *   sent yet (`closed`, its sentence). What code asked (version 1, a brief
 *   edit) shows only its answer.
 *
 * Controlled: the changes (newest first) and the pointing come in; every
 * act goes out.
 */
import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  type Change,
  type ChangeAnswer,
  RUNNING,
  isOpen,
} from "../api/changes";
import { type Shortfall, priceAsk, shortfallOf } from "../api/client";
import { BOX, answerLines, fillAll, phaseLine } from "../copy/changes";
import { Button, Card } from "../ui";
import { PriceConfirm, TopUp } from "../ui/Credits";
import { TaskReview } from "./TaskReview";
import { ChangeRecovery } from "./ChangeRecovery";
import { ChangeQuestion } from "./Question";
import { TemplateCard, templateCardOf } from "./TemplateCard";

export interface PointingChip {
  key: string;
  label: string;
  onClear(): void;
}

export interface ChangeBoxProps {
  /** The ad's changes, newest first. */
  changes: Change[];
  /** The last `progress` of the version being baked. */
  progress: { fraction: number } | null;
  chips: PointingChip[];
  sending: boolean;
  /** A route's error, as a sentence. */
  error: string | null;
  /** Sends the words (`confirm`: the person's yes to the estimate). Its
   *  answer, when it asks for a yes, opens the confirm; its 402 the block. */
  onSend(text: string, confirm?: boolean): void | Promise<unknown>;
  onStop(n: number): void;
  onAnswer(n: number, answer: ChangeAnswer): void;
  onConfirm(n: number): void;
  /** The labels of a template's slots (the template card), or null. */
  templateSlots?(templateId: string): string[] | null;
  /** How many earlier changes to show. */
  history?: number;
  /** The film is being built: a change sent waits for it. */
  waiting?: boolean;
  /** Why nothing can be sent yet (the film is being written), or null. */
  closed?: string | null;
}

const say = (s: string, p: Record<string, unknown>) => fillAll(s, p) ?? s;

function Lines({ lines }: { lines: string[] }) {
  return (
    <>
      {lines.map((line, i) => (
        <p
          key={i}
          className={`m-0 break-words text-sm ${i === lines.length - 1 && line.startsWith("—") ? "text-slate-500" : "text-slate-800"}`}
        >
          {line}
        </p>
      ))}
    </>
  );
}

/** One change of the history: the words, then what came of them. */
function Exchange({ change }: { change: Change }) {
  const lines = answerLines(change);
  return (
    <li className="flex flex-col gap-1" data-testid={`change-${change.n}`}>
      {change.origin === "code" ? null : (
        <p className="m-0 self-end rounded-lg rounded-br-sm bg-slate-800 px-3 py-1.5 text-sm text-white">
          {change.text}
        </p>
      )}
      {lines.length ? (
        <div className="flex flex-col gap-0.5 rounded-lg rounded-bl-sm bg-slate-100 px-3 py-1.5">
          <Lines lines={lines} />
        </div>
      ) : null}
    </li>
  );
}

export function ChangeBox(props: ChangeBoxProps) {
  const [text, setText] = useState("");
  const [closedCard, setClosedCard] = useState<number | null>(null);
  const newest = props.changes[0] ?? null;
  const open = isOpen(newest) ? newest : null;
  const running = !!open && RUNNING.includes(open.state);
  const words = text.trim();
  const busy = props.sending || running || !!props.closed;
  /** The open change waits for the film being built. */
  const waits = !!props.waiting && open?.state === "queued";
  const history = props.changes
    .slice(open ? 1 : 0, (open ? 1 : 0) + (props.history ?? 6))
    .reverse();
  // the newest exchange is at the bottom of the history: keep it in view
  const historyRef = useRef<HTMLOListElement>(null);
  const lastSeen = history.length
    ? `${history[history.length - 1].n}:${history[history.length - 1].state}`
    : "";
  useEffect(() => {
    const list = historyRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [lastSeen]);

  // §15.1: the words waiting for the person's yes to their estimate, and the
  // block when the balance is below it
  const [priced, setPriced] = useState<{
    text: string;
    credits: number;
  } | null>(null);
  const [short, setShort] = useState<Shortfall | null>(null);
  const deliver = async (said: string, confirm: boolean) => {
    setPriced(null);
    setShort(null);
    try {
      const answer = await props.onSend(said, confirm);
      const ask = priceAsk(answer);
      if (ask) setPriced({ text: said, credits: ask.estimate });
    } catch (error) {
      const missing = shortfallOf(error);
      if (missing) {
        setShort(missing);
        // the words stay, to send once topped up
        setText((now) => now || said);
      }
    }
  };
  const sendWords = (said: string) => void deliver(said, false);

  const send = () => {
    if (!words || busy) return;
    sendWords(words);
    setText("");
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    send();
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const offerAuthor =
    newest &&
    !open &&
    newest.state === "cannot" &&
    newest.error_code === "no_fit" &&
    newest.error_params?.offer_author === true;

  // "Save this as a template" (P22-T05): the newest change made one; its card
  // stays until it is closed or another change is asked for.
  const templateCard =
    newest && !open && newest.state === "applied"
      ? templateCardOf(newest.says)
      : null;

  return (
    <Card className="flex flex-col gap-3 p-4 md:p-5" data-testid="change-box">
      <div className="flex flex-col gap-1">
        <h2 className="m-0 text-base font-bold text-slate-900">{BOX.title}</h2>
        <p className="m-0 text-sm text-slate-600">{BOX.hint}</p>
        {props.closed ? (
          <p className="m-0 text-sm text-slate-500" data-testid="box-closed">
            {props.closed}
          </p>
        ) : null}
      </div>

      {history.length ? (
        <ol
          ref={historyRef}
          aria-label={BOX.history}
          className="m-0 flex max-h-72 list-none flex-col gap-3 overflow-y-auto p-0"
        >
          {history.map((c) => (
            <Exchange key={c.n} change={c} />
          ))}
        </ol>
      ) : null}

      {open ? (
        <div className="flex flex-col gap-2" data-testid="change-open">
          <p className="m-0 self-end rounded-lg rounded-br-sm bg-slate-800 px-3 py-1.5 text-sm text-white">
            {open.text}
          </p>
          {running ? (
            <div
              role="status"
              className="flex items-center gap-3 rounded-lg bg-brand-tint/40 px-3 py-2"
              data-testid="change-phase"
            >
              <span
                aria-hidden="true"
                className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-brand"
              />
              <span className="min-w-0 flex-1 text-sm font-semibold text-slate-900">
                {waits ? BOX.waits : phaseLine(open, props.progress)}
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => props.onStop(open.n)}
              >
                {BOX.stop}
              </Button>
            </div>
          ) : null}
          {open.state === "answered" && open.question?.source === "tasks" ? (
            <TaskReview
              key={open.n}
              tasks={open.question.tasks ?? []}
              scenes={open.question.scene_names ?? {}}
              intent={open.question.intent}
              busy={props.sending}
              onConfirm={(tasks, interpretation) =>
                props.onAnswer(open.n, { tasks, ...interpretation })
              }
              onCancel={() => props.onStop(open.n)}
            />
          ) : open.state === "answered" && open.question ? (
            <ChangeQuestion
              key={open.n}
              change={open}
              busy={props.sending}
              onAnswer={(answer) => props.onAnswer(open.n, answer)}
            />
          ) : null}
          {open.state === "waiting_confirm" ? (
            <div
              className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
              data-testid="author-confirm"
            >
              <Lines lines={answerLines(open)} />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={props.sending}
                  onClick={() => props.onConfirm(open.n)}
                >
                  {say(BOX.confirm, { fee: open.estimate_credits ?? 0 })}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={props.sending}
                  onClick={() => props.onStop(open.n)}
                >
                  {BOX.notNow}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {templateCard && closedCard !== newest?.n ? (
        <TemplateCard
          card={templateCard}
          labels={props.templateSlots?.(templateCard.templateId) ?? null}
          onClose={() => setClosedCard(newest?.n ?? null)}
        />
      ) : null}

      {newest && !open && !props.closed ? (
        <ChangeRecovery
          key={newest.n}
          change={newest}
          busy={busy}
          onSend={sendWords}
          onChoose={setText}
          onEdit={setText}
        />
      ) : null}

      {offerAuthor ? (
        <div>
          <Button
            type="button"
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={() => sendWords(BOX.yesWords)}
          >
            {BOX.yesWrite}
          </Button>
        </div>
      ) : null}

      {priced && !open ? (
        <div className="flex flex-col gap-2" data-testid="change-priced">
          <p className="m-0 self-end rounded-lg rounded-br-sm bg-slate-800 px-3 py-1.5 text-sm text-white">
            {priced.text}
          </p>
          <PriceConfirm
            what="change"
            credits={priced.credits}
            busy={props.sending}
            onYes={() => void deliver(priced.text, true)}
            onNo={() => {
              setText((now) => now || priced.text);
              setPriced(null);
            }}
          />
        </div>
      ) : null}

      {short ? <TopUp shortfall={short} /> : null}

      <form className="flex flex-col gap-2" onSubmit={onSubmit}>
        {props.chips.length ? (
          <ul
            className="m-0 flex list-none flex-wrap gap-1.5 p-0"
            aria-label={BOX.about}
            data-testid="change-chips"
          >
            {props.chips.map((chip) => (
              <li key={chip.key}>
                <span className="inline-flex max-w-full items-center gap-1 rounded-pill border border-brand-light/60 bg-brand-tint/40 py-0.5 pl-2.5 pr-1 text-xs text-brand-dark">
                  <span className="truncate">{chip.label}</span>
                  <button
                    type="button"
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full hover:bg-white"
                    aria-label={say(BOX.leaveOut, { what: chip.label })}
                    onClick={chip.onClear}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        <textarea
          className="tb-input min-h-[4.5rem] w-full resize-y"
          aria-label={BOX.label}
          placeholder={BOX.placeholder}
          value={text}
          maxLength={2000}
          rows={2}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          {props.error ? (
            <p role="alert" className="m-0 text-sm text-slate-700">
              {props.error}
            </p>
          ) : (
            <span />
          )}
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={busy || !words}
          >
            {props.sending ? BOX.sending : BOX.send}
          </Button>
        </div>
      </form>
    </Card>
  );
}
